import { normalizeCommitmentDetails } from './commitment';
import { normalizeInstrumentExperiences } from './instrumentExperience';
import { getLoadedProfilePlaysCount } from './profilePlaysCount';
import { normalizePlaysCount } from './playsCount';
import { getTimelineProfileStats } from './timelineProfileStats';
import { resolveMediaUrl } from './mediaUrl';
import { resolveUserDisplayName } from './userIdentity';
import {
  SEEKING_TYPES,
  isSeekingLookupType,
  normalizeSeekingPreferenceType,
} from '../constants/seeking';

export const PROFILE_EDITORIAL_SECTION_IDS = {
  INFO: 'info',
  SEEKING: 'seeking',
  SOUND: 'sound',
  COMMITMENT: 'commitment',
  EVENTS: 'events',
  BANDS: 'bands',
  GALLERY: 'gallery',
  MEDIA: 'media',
  FRIENDS: 'friends',
  POSTS: 'posts',
};

export const PROFILE_EDITORIAL_STAT_IDS = {
  PLAYS: 'plays',
  FOLLOWERS: 'followers',
  BANDS: 'bands',
  FRIENDS: 'friends',
  EVENTS: 'events',
  POSTS: 'posts',
};

const PROFILE_EDITORIAL_STAT_ORDER = [
  PROFILE_EDITORIAL_STAT_IDS.PLAYS,
  PROFILE_EDITORIAL_STAT_IDS.FOLLOWERS,
  PROFILE_EDITORIAL_STAT_IDS.BANDS,
  PROFILE_EDITORIAL_STAT_IDS.FRIENDS,
  PROFILE_EDITORIAL_STAT_IDS.EVENTS,
  PROFILE_EDITORIAL_STAT_IDS.POSTS,
];

const COMMITMENT_DAY_MAP = {
  monday: 'mon',
  tuesday: 'tue',
  wednesday: 'wed',
  thursday: 'thu',
  friday: 'fri',
  saturday: 'sat',
  sunday: 'sun',
};

export const createProfileEditorialSectionIds = (prefix = '') => {
  if (!prefix) {
    return PROFILE_EDITORIAL_SECTION_IDS;
  }
  return Object.entries(PROFILE_EDITORIAL_SECTION_IDS).reduce((acc, [key, value]) => ({
    ...acc,
    [key]: `${prefix}-${value}`,
  }), {});
};

export const buildProfileEditorialTabs = (
  t,
  sectionIds = PROFILE_EDITORIAL_SECTION_IDS,
  {
    includeEvents = false,
    includeFriends = true,
    includeSeeking = true,
  } = {},
) => [
  { id: sectionIds.INFO, label: t('home.editorial.tabs.info', 'Info') },
  ...(includeSeeking ? [{ id: sectionIds.SEEKING, label: t('home.editorial.tabs.seeking', 'Seeking') }] : []),
  { id: sectionIds.SOUND, label: t('home.editorial.tabs.sound', 'Sound & gear') },
  { id: sectionIds.COMMITMENT, label: t('home.editorial.tabs.commitment', 'Commitment') },
  ...(includeEvents ? [{ id: sectionIds.EVENTS, label: t('home.editorial.tabs.events', 'Events') }] : []),
  { id: sectionIds.BANDS, label: t('home.editorial.tabs.bands', 'Bands') },
  { id: sectionIds.GALLERY, label: t('home.editorial.tabs.gallery', 'Gallery') },
  { id: sectionIds.MEDIA, label: t('home.editorial.tabs.media', 'Media') },
  { id: sectionIds.POSTS, label: t('home.editorial.tabs.posts', 'Posts') },
  ...(includeFriends ? [{ id: sectionIds.FRIENDS, label: t('home.editorial.tabs.connections', 'Connections') }] : []),
];

export const parseSeekingDetails = (raw) => {
  if (!raw || typeof raw !== 'string' || !raw.trim()) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    const members = Array.isArray(parsed?.members) ? parsed.members : [];
    const bandStyles = Array.isArray(parsed?.bandStyles) ? parsed.bandStyles : [];
    const notes = typeof parsed?.notes === 'string' ? parsed.notes : '';
    const type = normalizeSeekingPreferenceType(parsed?.type, SEEKING_TYPES.MEMBERS);
    if (!isSeekingLookupType(type)) {
      return null;
    }

    return {
      type,
      members,
      bandStyles,
      notes,
      openToJam: parsed?.openToJam === false ? false : true,
    };
  } catch {
    return null;
  }
};

export const yearsToLevel = (yearsValue) => {
  if (!yearsValue) return 'Beginner';
  const value = String(yearsValue).toLowerCase();
  if (value.includes('pro')) return 'Professional';
  if (value.includes('adv')) return 'Advanced';
  if (value.includes('int')) return 'Intermediate';
  if (value.includes('beg')) return 'Beginner';
  return yearsValue;
};

export const splitToLines = (raw) => {
  if (!raw || typeof raw !== 'string') return [];
  return raw
    .split(/\r?\n|,|;/)
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 6);
};

export const formatLongDate = (date, locale = 'en') => {
  if (!date) return '';
  try {
    const value = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(value.getTime())) return '';
    return value.toLocaleString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

export const formatLocationLabel = (location, locationDisplay) => {
  if (locationDisplay && typeof locationDisplay === 'string') return locationDisplay;
  if (location && typeof location === 'object') {
    const parts = [
      location.city,
      location.stateOrRegion || location.region || location.state,
      location.country,
    ].filter(Boolean);
    return parts.join(', ');
  }
  if (typeof location === 'string' && location.trim()) return location.trim();
  return '';
};

export const formatNumber = (value) => {
  if (value == null) return '';
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  if (number >= 1000) {
    return `${(number / 1000).toFixed(number >= 10000 ? 0 : 1)}K`;
  }
  return number.toLocaleString();
};

export const stripFileExtension = (name) => {
  if (!name || typeof name !== 'string') return '';
  return name.replace(/\.[^.]+$/, '').trim();
};

export const formatFileSize = (sizeBytes) => {
  const size = Number(sizeBytes);
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(size >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
  if (size >= 1024) return `${Math.round(size / 1024)} KB`;
  return `${size} B`;
};

export const formatTrackMeta = (track, locale) => {
  const parts = [];
  const size = formatFileSize(track?.sizeBytes);
  if (size) parts.push(size);
  if (track?.uploadedAt) {
    const uploaded = formatLongDate(track.uploadedAt, locale);
    if (uploaded) parts.push(uploaded);
  }
  return parts.join(' / ');
};

export const buildTrackSeed = (track, fallback) => {
  const source = track?.objectKey || track?.id || String(fallback);
  return Array.from(String(source)).reduce((sum, char) => sum + char.charCodeAt(0), 0) % 97 || 1;
};

export const getTrackIdentity = (track) => track?.objectKey || track?.id || null;

export const normalizeTrackPlaysCount = (value) => {
  return normalizePlaysCount(value);
};

export const formatTrackPlaysCount = (playsCount, t, {
  key = 'home.editorial.media.playsCount',
} = {}) => {
  const count = normalizeTrackPlaysCount(playsCount);
  if (count === null) {
    return null;
  }
  const defaultValue = count === 1 ? '1 play' : `${count} plays`;
  return typeof t === 'function'
    ? t(key, { count, defaultValue })
    : defaultValue;
};

export const buildMediaTrackRows = (tracks, {
  locale = 'en',
  playingTrackId = null,
  trackProgressById = {},
  t,
  untitledTrackKey = 'home.editorial.media.untitledTrack',
  untitledTrackDefault = 'Untitled track',
  playsCountKey = 'home.editorial.media.playsCount',
} = {}) => (
  (Array.isArray(tracks) ? tracks : []).map((track, index) => {
    const trackId = getTrackIdentity(track) || `${index}`;
    const plays = formatTrackPlaysCount(track?.playsCount, t, { key: playsCountKey });
    return {
      ...track,
      id: trackId,
      title: track?.title || (
        typeof t === 'function'
          ? t(untitledTrackKey, untitledTrackDefault)
          : untitledTrackDefault
      ),
      meta: formatTrackMeta(track, locale),
      cover: track?.artworkUrl ? resolveMediaUrl(track.artworkUrl) : null,
      progress: trackProgressById?.[trackId] || 0,
      seed: buildTrackSeed(track, index + 1),
      isPlaying: playingTrackId === trackId,
      plays: plays ?? track?.plays,
    };
  })
);

export const applyTrackPlaysCountByIdentity = (tracks, track, playsCount) => {
  const count = normalizeTrackPlaysCount(playsCount);
  const trackId = getTrackIdentity(track);
  if (count === null || !trackId || !Array.isArray(tracks)) {
    return tracks;
  }
  let didUpdate = false;
  const nextTracks = tracks.map((item) => {
    if (getTrackIdentity(item) !== trackId) {
      return item;
    }
    didUpdate = true;
    return { ...item, playsCount: count };
  });
  return didUpdate ? nextTracks : tracks;
};

export const getMediaPhotoIdentity = (photo) => photo?.objectKey || photo?.url || null;

export const reorderMediaPhotosByIdentity = (photos, orderedPhotoIds) => {
  const currentPhotos = Array.isArray(photos) ? photos : [];
  if (!Array.isArray(orderedPhotoIds) || orderedPhotoIds.length !== currentPhotos.length) {
    return currentPhotos;
  }
  const photosById = new Map();
  currentPhotos.forEach((photo) => {
    const id = getMediaPhotoIdentity(photo);
    if (id) {
      photosById.set(id, photo);
    }
  });
  const nextPhotos = orderedPhotoIds.map((id) => photosById.get(id)).filter(Boolean);
  return nextPhotos.length === currentPhotos.length ? nextPhotos : currentPhotos;
};

export const moveTrackByIdentity = (tracks, trackIdentity, direction) => {
  const currentTracks = Array.isArray(tracks) ? tracks : [];
  const currentIndex = currentTracks.findIndex((track) => getTrackIdentity(track) === trackIdentity);
  if (currentIndex < 0) {
    return currentTracks;
  }
  const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
  if (targetIndex < 0 || targetIndex >= currentTracks.length) {
    return currentTracks;
  }
  const nextTracks = [...currentTracks];
  const [track] = nextTracks.splice(currentIndex, 1);
  nextTracks.splice(targetIndex, 0, track);
  return nextTracks;
};

export const buildProfileEditorialStatsItems = ({
  playsCount,
  followersCount = null,
  includePlaysStat = true,
  includeFollowersStat = false,
  bandMemberships = null,
  includeBandsStat,
  includeFriendsStat = true,
  includeEventsStat = true,
  friends = [],
  timelineStats = {},
  t,
}) => {
  const hasBandMembershipData = Array.isArray(bandMemberships);
  const shouldIncludeBandsStat = includeBandsStat ?? hasBandMembershipData;
  const normalizedBandMemberships = hasBandMembershipData ? bandMemberships : [];
  const hasFollowersCount = followersCount !== null
    && followersCount !== undefined
    && Number.isFinite(Number(followersCount));
  const hasPlaysCount = playsCount !== null
    && playsCount !== undefined
    && Number.isFinite(Number(playsCount));
  const unknownStatValue = t('home.editorial.stats.unknown', '-');
  const statsById = {
    [PROFILE_EDITORIAL_STAT_IDS.PLAYS]: {
      id: PROFILE_EDITORIAL_STAT_IDS.PLAYS,
      value: hasPlaysCount ? formatNumber(playsCount) : unknownStatValue,
      label: t('home.editorial.stats.plays', 'Plays'),
    },
    [PROFILE_EDITORIAL_STAT_IDS.FOLLOWERS]: {
      id: PROFILE_EDITORIAL_STAT_IDS.FOLLOWERS,
      value: hasFollowersCount ? formatNumber(followersCount) : unknownStatValue,
      label: t('home.editorial.stats.followers', 'Followers'),
    },
    [PROFILE_EDITORIAL_STAT_IDS.BANDS]: {
      id: PROFILE_EDITORIAL_STAT_IDS.BANDS,
      value: hasBandMembershipData ? formatNumber(normalizedBandMemberships.length) : unknownStatValue,
      label: t('home.editorial.stats.bands', 'Bands'),
    },
    [PROFILE_EDITORIAL_STAT_IDS.FRIENDS]: {
      id: PROFILE_EDITORIAL_STAT_IDS.FRIENDS,
      value: formatNumber((friends || []).length),
      label: t('home.editorial.stats.friends', 'Friends'),
    },
    [PROFILE_EDITORIAL_STAT_IDS.EVENTS]: {
      id: PROFILE_EDITORIAL_STAT_IDS.EVENTS,
      value: formatNumber(timelineStats.eventsCount),
      label: t('home.editorial.stats.events', 'Events'),
    },
    [PROFILE_EDITORIAL_STAT_IDS.POSTS]: {
      id: PROFILE_EDITORIAL_STAT_IDS.POSTS,
      value: formatNumber(timelineStats.postsCount),
      label: t('home.editorial.stats.posts', 'Posts'),
    },
  };

  return PROFILE_EDITORIAL_STAT_ORDER
    .filter((id) => id !== PROFILE_EDITORIAL_STAT_IDS.PLAYS || includePlaysStat)
    .filter((id) => id !== PROFILE_EDITORIAL_STAT_IDS.FOLLOWERS || includeFollowersStat)
    .filter((id) => id !== PROFILE_EDITORIAL_STAT_IDS.BANDS || shouldIncludeBandsStat)
    .filter((id) => id !== PROFILE_EDITORIAL_STAT_IDS.FRIENDS || includeFriendsStat)
    .filter((id) => id !== PROFILE_EDITORIAL_STAT_IDS.EVENTS || includeEventsStat)
    .map((id) => statsById[id]);
};

export const sortMediaPhotosNewestFirst = (photos) => [...photos].sort((a, b) => {
  const aTime = a?.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
  const bTime = b?.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
  return bTime - aTime;
});

export const resolveProfileEditorialDisplayName = ({
  profileSummary,
  username,
  displayNameFallback = '',
} = {}) => {
  if (!profileSummary) {
    return null;
  }
  return resolveUserDisplayName({
    username,
    profile: profileSummary?.profile || {},
  }, displayNameFallback);
};

export const resolveProfileEditorialHandle = ({
  profileSummary,
  username,
} = {}) => {
  if (!profileSummary || typeof username !== 'string') {
    return '';
  }
  const normalizedUsername = username.trim().replace(/^@+/, '');
  return normalizedUsername ? `@${normalizedUsername}` : '';
};

export const buildProfileEditorialViewModel = ({
  profileSummary,
  username,
  displayNameFallback = '',
  posts = [],
  timelineEntities = [],
  followersCount = null,
  includePlaysStat = true,
  includeFollowersStat = false,
  bandMemberships = null,
  includeBandsStat,
  includeFriendsStat = true,
  includeEventsStat = true,
  friends = [],
  profileTracks = [],
  userMediaPhotos = [],
  trackProgressById = {},
  playingTrackId = null,
  t,
  locale = 'en',
}) => {
  const profile = profileSummary?.profile || {};
  const profileDisplayName = resolveProfileEditorialDisplayName({
    profileSummary,
    username,
    displayNameFallback,
  });
  const handle = resolveProfileEditorialHandle({ profileSummary, username });
  const instrumentExperiences = normalizeInstrumentExperiences(profile.instrumentExperience);
  const skills = instrumentExperiences.map((exp) => ({
    id: exp.id,
    instrument: exp.instrument || 'Instrument',
    level: yearsToLevel(exp.years),
  }));
  const mainInstrument = instrumentExperiences[0]?.instrument || '';
  const seekingDetails = parseSeekingDetails(profile.seeking);
  const commitmentDetails = normalizeCommitmentDetails(profile.commitment);
  const weekSchedule = (commitmentDetails.schedule || []).map((entry) => ({
    day: COMMITMENT_DAY_MAP[entry.day] || entry.day,
    slots: entry.slots,
  }));
  const influencesList = splitToLines(profile.influences);
  const equipmentList = splitToLines(profile.equipment);
  const locationLabel = formatLocationLabel(profile.location, profile.locationDisplay);
  const playsCount = getLoadedProfilePlaysCount(profileSummary);
  const timelineStats = getTimelineProfileStats([...(Array.isArray(posts) ? posts : []), ...(Array.isArray(timelineEntities) ? timelineEntities : [])]);
  const memberSinceLabel = formatLongDate(profileSummary?.createdAt, locale);
  const seekingHeroLabel = seekingDetails
    ? (seekingDetails.type === 'band'
      ? t('home.editorial.hero.seekingBand', 'Looking for a Band')
      : t('home.editorial.hero.seekingMembers', 'Looking for Members'))
    : null;
  const seekingGenres = (seekingDetails?.bandStyles || [])
    .map((style) => style?.style || style?.label)
    .filter(Boolean);
  const seekingQuote = seekingDetails?.notes || '';
  const statsItems = buildProfileEditorialStatsItems({
    playsCount,
    followersCount,
    includePlaysStat,
    includeFollowersStat,
    bandMemberships,
    includeBandsStat,
    includeFriendsStat,
    includeEventsStat,
    friends,
    timelineStats,
    t,
  });
  const heroTopJoinedLabel = memberSinceLabel
    ? `${t('home.editorial.hero.joined', 'Joined')} ${memberSinceLabel}`
    : '';
  const mediaTracks = buildMediaTrackRows(profileTracks, {
    locale,
    playingTrackId,
    t,
    trackProgressById,
  });
  const mediaPhotos = (Array.isArray(userMediaPhotos) ? userMediaPhotos : [])
    .map((photo, index) => {
      const rawUrl = photo?.url || '';
      const resolvedUrl = resolveMediaUrl(rawUrl);
      const caption = stripFileExtension(photo?.originalFileName || '');
      return {
        id: getMediaPhotoIdentity(photo) || `media-photo-${index}`,
        objectKey: photo?.objectKey || null,
        url: resolvedUrl,
        thumb: resolvedUrl,
        caption,
        span: index === 0 ? 'wide' : undefined,
      };
    });

  return {
    profileDisplayName,
    handle,
    instrumentExperiences,
    skills,
    mainInstrument,
    seekingDetails,
    commitmentDetails,
    weekSchedule,
    influencesList,
    equipmentList,
    locationLabel,
    playsCount,
    timelineStats,
    memberSinceLabel,
    seekingHeroLabel,
    seekingGenres,
    seekingQuote,
    statsItems,
    heroTopJoinedLabel,
    mediaTracks,
    mediaPhotos,
  };
};
